import { show as showIntercom } from "@intercom/messenger-js-sdk";
import { ChevronLeft, ChevronRight, QuestionMark, Refresh } from "@mui/icons-material";
import {
    Box,
    BoxProps,
    ClickAwayListener,
    css,
    Divider,
    IconButton,
    ListItemIcon,
    ListItemText,
    MenuItem,
    Popper,
    Stack,
    styled,
    Tooltip,
    Typography,
    useTheme,
} from "@mui/material";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { useAppDispatch, useAppSelector } from "app/redux-store-interactions";
import { HudPanel } from "components/hudPanel";
import IconButtonExt from "components/iconButtonExt";
import { FeatureGroupKey, featureGroups, FeatureType, Widget, WidgetKey } from "config/features";
import { groupSorting, widgetSorting } from "features/groupedWidgetList/sorting";
import { useOpenWidget } from "hooks/useOpenWidget";
import { useRefreshAndRestoreState } from "hooks/useRefreshAndRestoreState";
import NovorenderIcon from "media/icons/novorender-small.svg?react";
import { selectUser } from "slices/authSlice";
import {
    explorerActions,
    selectEnabledWidgets,
    selectIsOnline,
    selectLockedWidgets,
    selectNewProjectVersionStatus,
    selectWidgetGroupPanelState,
    selectWidgetLayout,
    selectWidgets,
} from "slices/explorer";
import { isAppleDevice } from "utils/misc";

const sortedFeatureGroups = Object.values(featureGroups).sort((a, b) => {
    const idxA = groupSorting.indexOf(a.key);
    const idxB = groupSorting.indexOf(b.key);

    return (idxA === -1 ? groupSorting.length : idxA) - (idxB === -1 ? groupSorting.length : idxB);
});

export function WidgetGroupPanel() {
    const theme = useTheme();
    const state = useAppSelector(selectWidgetGroupPanelState);
    const layout = useAppSelector(selectWidgetLayout);
    const { open, expanded } = state;
    const dispatch = useAppDispatch();
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [activeGroup, setActiveSection] = useState<FeatureGroupKey | null>(null);
    const enabledWidgets = useAppSelector(selectEnabledWidgets);
    const lockedWidgets = useAppSelector(selectLockedWidgets);
    const openWidget = useOpenWidget();
    const user = useAppSelector(selectUser);
    const newProjectVersionStatus = useAppSelector(selectNewProjectVersionStatus);

    const closePopper = () => {
        setAnchorEl(null);
        setActiveSection(null);
    };

    const handleSectionClick = (e: React.MouseEvent<HTMLElement>, groupKey: FeatureGroupKey) => {
        if (groupKey !== activeGroup) {
            setActiveSection(groupKey);
            // Use parent element as anchor, because if we expand the panel -
            // anchor changes and tooltip flies away
            setAnchorEl(e.currentTarget.closest("[data-section-box]") as HTMLElement);
        } else {
            closePopper();
        }
    };

    const handleWidgetClick = (widgetKey: WidgetKey) => {
        closePopper();
        openWidget(widgetKey);
    };

    const nonEmptyGroups = useMemo(() => {
        return sortedFeatureGroups
            .map((group) => {
                const widgets = enabledWidgets.filter(
                    (widget) =>
                        (widget.type === FeatureType.Widget || widget.type === FeatureType.AdminWidget) &&
                        "groups" in widget &&
                        widget.groups.includes(group.key as never),
                );

                const sortAndFilterWidgets = (widgets: Widget[]) =>
                    widgets
                        .filter((widget) => !lockedWidgets.includes(widget.key))
                        .sort((a, b) => {
                            const idxA = widgetSorting.indexOf(a.key);
                            const idxB = widgetSorting.indexOf(b.key);

                            return (
                                (idxA === -1 ? widgetSorting.length : idxA) -
                                (idxB === -1 ? widgetSorting.length : idxB)
                            );
                        });

                return { group, widgets: sortAndFilterWidgets(widgets) };
            })
            .filter((e) => e.widgets.length > 0);
    }, [enabledWidgets, lockedWidgets]);

    return (
        <ClickAwayListener onClickAway={closePopper}>
            <Box
                sx={{
                    zIndex: 1060,
                    pointerEvents: "none",
                    position: "absolute",
                    right: 0,
                    bottom: 0,
                    height: open ? (layout.padWidgetsTop ? `calc(100% - ${theme.spacing(9)})` : "100%") : undefined,
                    display: "flex",
                    flexDirection: "column",
                }}
            >
                {open && (
                    <>
                        <HudPanel
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                transition: "all 0.2s",
                                pointerEvents: "auto",
                                flex: "auto",
                                borderBottomLeftRadius: 0,
                                borderBottomRightRadius: 0,
                                position: "relative",
                                overflow: "auto",
                            }}
                        >
                            <Stack
                                sx={{
                                    gap: 1,
                                    overflowX: "hidden",
                                    width: expanded
                                        ? theme.spacing(theme.customSpacing.widgetGroupPanelExpandedWidth - 4)
                                        : theme.spacing(8),
                                    transition: "width 0.2s",
                                    position: "relative",
                                    flex: "auto",
                                }}
                            >
                                <Box display="flex" justifyContent="center">
                                    <IconButton
                                        onClick={() =>
                                            dispatch(
                                                explorerActions.setWidgetGroupPanelState({
                                                    ...state,
                                                    expanded: !expanded,
                                                }),
                                            )
                                        }
                                    >
                                        {expanded ? <ChevronRight /> : <ChevronLeft />}
                                    </IconButton>
                                </Box>
                                <Divider sx={{ mt: -0.5 }} />
                                {nonEmptyGroups.map(({ group }) => (
                                    <Section
                                        key={group.key}
                                        groupKey={group.key}
                                        onClick={handleSectionClick}
                                        active={group.key === activeGroup}
                                        sidebarOpen={expanded}
                                    />
                                ))}

                                {user && <IntercomButton sidebarOpen={expanded} />}
                            </Stack>
                        </HudPanel>
                        <BottomPanel />

                        <Popper
                            open={anchorEl !== null}
                            anchorEl={anchorEl}
                            placement="left"
                            disablePortal
                            sx={{ zIndex: 1060, pointerEvents: "auto" }}
                            modifiers={[
                                // to avoid jumping when collapsing/expanding sidebar while the popper is open
                                {
                                    name: "computeStyles",
                                    options: {
                                        adaptive: false,
                                    },
                                },
                            ]}
                        >
                            <HudPanel sx={{ mr: 2 }}>
                                <SectionWidgets
                                    onSelect={handleWidgetClick}
                                    widgets={nonEmptyGroups.find((g) => g.group.key === activeGroup)?.widgets ?? []}
                                />
                            </HudPanel>
                        </Popper>
                    </>
                )}
                <Box position="relative">
                    <LogoBtn
                        onClick={() => {
                            closePopper();
                            dispatch(explorerActions.setWidgetGroupPanelState({ ...state, open: !open }));
                        }}
                        showNewVersionBtn={newProjectVersionStatus.available && newProjectVersionStatus.dismissed}
                    />
                </Box>
            </Box>
        </ClickAwayListener>
    );
}

function IntercomButton({ sidebarOpen }: { sidebarOpen: boolean }) {
    const { t } = useTranslation();

    return (
        <Tooltip title={t("support")} placement="left">
            <Box data-section-box>
                <SectionButton onClick={showIntercom} component={sidebarOpen ? "button" : "div"} active={false}>
                    {sidebarOpen ? (
                        <QuestionMark sx={{ ml: 1 }} />
                    ) : (
                        <IconButtonExt size="large">
                            <QuestionMark />
                        </IconButtonExt>
                    )}
                    <Typography fontWeight={600}>{t("support")}</Typography>
                </SectionButton>
            </Box>
        </Tooltip>
    );
}

function BottomPanel() {
    // complicated margins/width/heights and wrappers around the panel and svg are all for good shadows
    return (
        <Box sx={{ flex: "0 0 64px", display: "flex", zIndex: 1050, pointerEvents: "auto" }}>
            <Box sx={{ flex: "auto", overflow: "hidden", ml: "-10px", mb: "-10px" }}>
                <HudPanel
                    sx={{
                        height: "calc(100% - 10px)",
                        borderTopLeftRadius: 0,
                        borderTopRightRadius: 0,
                        borderBottomRightRadius: 0,
                        ml: "10px",
                        mb: "10px",
                        filter: "drop-shadow(0px 2px 4px rgba(0,0,0,0.2)) drop-shadow(0px 4px 5px rgba(0,0,0,0.14)) drop-shadow(0px 1px 10px rgba(0,0,0,0.12))",
                    }}
                    elevation={0}
                ></HudPanel>
            </Box>
            <Box sx={{ overflow: "hidden", width: "74px", mr: "-10px", mb: "-10px" }}>
                <svg
                    width="64"
                    height="64"
                    viewBox="0 0 64 64"
                    fill="white"
                    style={{
                        filter: "drop-shadow(0px 2px 4px rgba(0,0,0,0.2)) drop-shadow(0px 4px 5px rgba(0,0,0,0.14)) drop-shadow(0px 1px 10px rgba(0,0,0,0.12))",
                    }}
                >
                    <path d="m 0 0 v 64 a 16 16 0 0 0 16 -16 a 36 36 0 0 1 32 -32 A 16 16 0 0 0 64 0 z" />
                </svg>
            </Box>
        </Box>
    );
}

function Section({
    groupKey,
    onClick,
    active,
    sidebarOpen,
}: {
    groupKey: FeatureGroupKey;
    onClick: (e: React.MouseEvent<HTMLElement>, groupKey: FeatureGroupKey) => void;
    active: boolean;
    sidebarOpen: boolean;
}) {
    const { t } = useTranslation();
    const { nameKey, Icon } = featureGroups[groupKey];
    const name = t(nameKey);

    return (
        <Tooltip title={active ? "" : name} placement="left">
            <Box data-section-box>
                <SectionButton
                    onClick={(e) => onClick(e, groupKey)}
                    active={sidebarOpen && active}
                    component={sidebarOpen ? "button" : "div"}
                >
                    {sidebarOpen ? (
                        <Icon sx={{ ml: 1 }} />
                    ) : (
                        <IconButtonExt
                            size="large"
                            active={active}
                            sx={sidebarOpen && active ? { background: "transparent" } : {}}
                        >
                            <Icon />
                        </IconButtonExt>
                    )}
                    <Typography fontWeight={600}>{name}</Typography>
                </SectionButton>
            </Box>
        </Tooltip>
    );
}

const SectionButton = styled(Box, { shouldForwardProp: (prop) => prop !== "active" })<BoxProps & { active: boolean }>(
    ({ theme, active }) => css`
        width: 100%;
        display: flex;
        align-items: center;
        text-align: start;
        border: 0;
        padding-right: ${theme.spacing(2)};
        padding-left: ${theme.spacing(1)};
        gap: ${theme.spacing(2)};
        cursor: pointer;
        min-height: 48px;
        background: ${active ? theme.palette.primary.main : "transparent"};
        transition: background 0.2s;
        color: ${active ? theme.palette.primary.contrastText : theme.palette.action.active};
        border-radius: ${theme.shape.borderRadius}px;

        &:hover {
            background: ${active ? theme.palette.primary.dark : "transparent"};
        }
    `,
);

function SectionWidgets({ onSelect, widgets }: { onSelect: (widgetKey: WidgetKey) => void; widgets: Widget[] }) {
    const { t } = useTranslation();
    const activeWidgets = useAppSelector(selectWidgets);
    const isOnline = useAppSelector(selectIsOnline);

    return (
        <>
            {widgets.map((widget) => {
                const activeElsewhere = activeWidgets.includes(widget.key);
                const unavailable = !isOnline && "offline" in widget && !widget.offline;
                const { Icon } = widget;

                return (
                    <MenuItem
                        key={widget.key}
                        disabled={activeElsewhere || unavailable}
                        onClick={() => onSelect(widget.key)}
                    >
                        <ListItemIcon>
                            <Icon />
                        </ListItemIcon>
                        <ListItemText>{t(widget.nameKey)}</ListItemText>
                        {"keyboardShortcut" in widget && (
                            <Typography color="grey" ml={2}>
                                {isAppleDevice && "mac" in widget.keyboardShortcut
                                    ? widget.keyboardShortcut.mac
                                    : widget.keyboardShortcut.default}
                            </Typography>
                        )}
                    </MenuItem>
                );
            })}
        </>
    );
}

export function LogoBtn({ onClick, showNewVersionBtn }: { onClick: () => void; showNewVersionBtn?: boolean }) {
    const { t } = useTranslation();
    const isOnline = useAppSelector(selectIsOnline);
    const theme = useTheme();
    const refresh = useRefreshAndRestoreState();

    return (
        <Box
            position="relative"
            sx={{
                pointerEvents: "auto",
                position: "absolute",
                bottom: -14,
                right: -14,
                boxShadow: 4,
                borderRadius: "50%",
                zIndex: 1052,
            }}
        >
            <IconButton
                onClick={onClick}
                sx={{
                    backgroundColor: isOnline ? theme.palette.primary.main : theme.palette.secondary.main,
                    "&:hover": {
                        backgroundColor: isOnline ? theme.palette.primary.dark : theme.palette.secondary.dark,
                    },
                    width: 56,
                    height: 56,
                }}
            >
                <NovorenderIcon />
            </IconButton>
            {showNewVersionBtn && (
                <Tooltip title={t("newProjectVersionMessage")} placement="left">
                    <IconButton
                        sx={{
                            position: "absolute",
                            top: -8,
                            left: -8,
                            backgroundColor: theme.palette.info.main,
                            "&:hover": {
                                backgroundColor: theme.palette.info.dark,
                            },
                            color: theme.palette.getContrastText(theme.palette.info.main),
                            width: 28,
                            height: 28,
                            border: `2px solid ${theme.palette.getContrastText(theme.palette.info.main)}`,
                        }}
                        onClick={refresh}
                    >
                        <Refresh fontSize="small" />
                    </IconButton>
                </Tooltip>
            )}
        </Box>
    );
}
